import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

function Section({ number, title, children }) {
  return (
    <div className="flex space-x-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-vextaris-blue/10 border border-vextaris-blue/30 flex items-center justify-center text-vextaris-blue text-sm font-medium">
        {number}
      </div>
      <div className="flex-1 pb-8">
        <h3 className="text-white font-medium mb-2">{title}</h3>
        <div className="text-gray-400 text-sm leading-relaxed space-y-2">{children}</div>
      </div>
    </div>
  );
}

export default function TokensGuide() {
  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/guide" className="inline-flex items-center text-gray-500 hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeftIcon className="w-4 h-4 mr-2" />
        Back to Guide
      </Link>
      <h1 className="text-3xl font-bold text-white mb-8">Creating Tokens on VexForge</h1>

      <p className="text-gray-400 leading-relaxed mb-10">
        Vextaris uses the VSC-7 standard for fungible tokens. Token creation is a native protocol operation
        — there is no smart contract to write or deploy. Fill out the form, sign the transaction, and your
        token exists on-chain with full metadata, reputation scoring, and explorer visibility.
      </p>

      <div className="space-y-2">
        <Section number={1} title="Navigate to Create Token">
          <p>
            Go to <Link to="/create" className="text-vextaris-blue hover:underline">Create Token</Link> from
            the sidebar or home page. You will need a connected wallet with at least 100 VXS to cover
            the creation fee.
          </p>
        </Section>

        <Section number={2} title="Fill in token details">
          <p>
            Enter a <span className="text-white">name</span> (e.g. "Shogun Token"),
            a <span className="text-white">symbol</span> (e.g. "SHGN"), and
            the <span className="text-white">decimals</span> value.
          </p>
          <p>
            The standard on Vextaris is <span className="text-white">9 decimals</span>, matching VXS.
            Unless you have a specific reason (e.g. stablecoin with 6), use 9 for maximum compatibility
            with wallets, DEX pools, and the Intent system.
          </p>
        </Section>

        <Section number={3} title="Set initial supply">
          <p>
            Enter the total number of tokens to mint. This is the <span className="text-white">display amount</span>,
            not the raw on-chain value. If you enter 1,000,000 with 9 decimals, the chain stores
            1,000,000,000,000,000 (1e15) raw units.
          </p>
          <p>
            You can mint more later if you retain mint authority. Revoking mint authority makes the supply
            permanently fixed — this significantly increases reputation score.
          </p>
        </Section>

        <Section number={4} title="Add metadata">
          <p>
            Fill in <span className="text-white">description</span>, <span className="text-white">image URL</span>,
            and <span className="text-white">social links</span> (website, Twitter/X, Telegram, Discord).
            Metadata is stored on-chain and displayed on VexScan and VexSpark.
          </p>
          <p>
            Tokens with complete metadata receive a higher reputation score. Social links are verified
            by the community — adding them early builds trust.
          </p>
        </Section>

        <Section number={5} title="Connect wallet and submit">
          <p>
            Click <span className="text-white">Connect Wallet</span> if not already connected. Review the
            transaction summary, then sign and submit. The token is created in a single block — typically
            under 2 seconds.
          </p>
          <p>
            After creation, you will see the token mint address (a Vx-prefixed address). Save this — you
            will need it for presales, DEX pools, and transfers.
          </p>
        </Section>

        <Section number={6} title="Lock metadata (optional)">
          <p>
            Navigate to your token's detail page and click <span className="text-white">Lock Metadata</span>.
            This makes the token's name, symbol, description, and image permanently immutable.
          </p>
          <p>
            Locking metadata is a strong trust signal. It tells holders that the project cannot
            rug-pull by changing the token identity after launch.
          </p>
        </Section>

        <Section number={7} title="Create a presale (optional)">
          <p>
            If you want to distribute tokens through a presale with escrow protection, head
            to <Link to="/guide/presales" className="text-vextaris-blue hover:underline">the Presales guide</Link> for
            the full walkthrough.
          </p>
        </Section>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-vextaris-darker border border-gray-800 rounded-xl p-6">
        <h3 className="text-white font-medium mb-3">Tips</h3>
        <ul className="text-gray-400 text-sm space-y-2 list-disc list-inside">
          <li>Use <span className="text-white">9 decimals</span> for consistency with VXS and the broader Vextaris ecosystem.</li>
          <li>Lock metadata early — it cannot be undone, but it significantly boosts reputation score.</li>
          <li>Add social links. They are visible on VexScan and help users verify your project.</li>
          <li>Token creation costs approximately <span className="text-white">100 VXS</span>. Get testnet VXS from the <a href="https://vexswap.xyz" target="_blank" rel="noopener noreferrer" className="text-vextaris-blue hover:underline">faucet</a>.</li>
          <li>Revoking mint authority makes supply permanently fixed and raises reputation further.</li>
        </ul>
      </div>

      {/* Docs link */}
      <div className="mt-6 text-sm text-gray-500">
        For the full token specification, see{' '}
        <a href="https://docs.vextaris.io/token-standards/overview#create-a-token" target="_blank" rel="noopener noreferrer" className="text-vextaris-blue hover:underline">
          docs.vextaris.io/token-standards/overview#create-a-token
        </a>.
      </div>
    </div>
  );
}
